import moment from "moment";
import "./Billing.scss";
import { getAccessTokenSilently } from "utils/functions";
import {
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Pagination } from "@material-ui/lab";
import { ReactComponent as PayNowSvg } from "assets/icons/Property.svg";
import { Loader } from "components";
import Button from "components/button/Button";
import NewDialog from "components/NewDialog/Dialog";
import { MyCheckbox } from "components";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import useGetUserAccount from "hook/useGetUserAccount";
import { debounce } from "lodash";
import React, { memo, useCallback, useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import FinanceService from "../../Lib/API/Finance";
import Action from "components/ActionPopOver";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

/* ── Shared styled cells (matches ChargeTable style) ── */
const TableCellHead = withStyles(() => ({
  root: {
    borderBottom: "none",
    background: "var(--Blue-10, #F3F6F9)",
    ...Fonts.GeneralSmallLabelFont,
    color: FontColors.TableHeader,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "12px 16px",
    whiteSpace: "nowrap",
  },
}))(TableCell);

const TableCellBody = withStyles(() => ({
  root: {
    height: "65px",
    fontFamily: Fonts.GeneralSmallBodyFont.fontFamily,
    fontSize: Fonts.GeneralSmallBodyFont.fontSize,
    color: FontColors.Slate334155,
    borderBottom: "1px solid #F8FAFC",
    padding: "12px 16px",
    verticalAlign: "middle",
  },
}))(TableCell);

const statusColor = (chargeType) => {
  if (chargeType === "charge" || chargeType === "donation" || chargeType === "credit_on_account")
    return "#FBA12C";
  if (chargeType === "payment") return "#81DC91";
  return "#64748B";
};

const ChargesTable = ({
  page,
  setPage,
  filters,
  sortCharged,
  list,
  batchTable,
  paymentExpandable,
  setPaymentExpandable,
  setBatchList,
  batchList,
  resetChecks,
  setResetchecks,
  viewDocumentHandler,
  downloadFileHandler,
  setGoSetting,
  goSetting,
}) => {
  const [currentAccount, setCurrentAccount] = useState(null);
  // rowDocuments[rowId] = { invoice: doc|null, receipt: doc|null }
  const [rowDocuments, setRowDocuments] = useState({});
  const isMobile = useMediaQuery("(max-width:600px)");
  const [financeCheckIds, setFinanceCheckIds] = useState([]);
  const [check, setCheck] = useState([]);
  const [productData, setProductData] = useState();
  const [count, setCount] = useState();
  const [checkData, setCheckData] = useState([]);
  const [savetribute, setsavetribute] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [openInstallmentModal, setOpenInstallmentModal] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => {
    setCheckData([]);
    setCheck([]);
    setFinanceCheckIds([]);
    setCurrentAccount(null);
  }, [batchList]);

  useEffect(() => {
    if (resetChecks === true) {
      setCheckData([]);
      setCheck([]);
      setResetchecks(false);
    }
  }, [resetChecks]);

  useEffect(() => {
    setPage(0);
  }, []);

  const fetchMyAPI = async () => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    await FinanceService.getAllTransactions(token, page + 1, 30, filters)
      .then((res) => {
        setProductData(res?.payload?.data);
        setCount(res?.payload?.pagination?.count);
        setLoading(false);
        setGoSetting(false);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
      });
  };

  const accountData = useGetUserAccount({ member: true });
  let accountID = localStorage.getItem("accountID");

  useEffect(() => {
    if (!accountData) return;
    accountID = accountData?.user?.permissions?.linked_account?._id;
    if (balance === null) fetchAmountApi();
  }, [accountData, balance]);

  const fetchAmountApi = async () => {
    const token = await getAccessTokenSilently();
    setLoading(true);
    await FinanceService.getAmountByAccountId(token, accountID)
      .then((res) => {
        setBalance(res);
        setLoading(false);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
      });
  };

  /* fetch invoice + receipt for a row when its 3-dots is opened */
  const fetchRowDocuments = useCallback(async (row) => {
    const rowId = row._id;
    if (row?.charge?.status !== "payment") return;
    if (rowDocuments[rowId]) return; // already fetched
    const token = await getAccessTokenSilently();
    const [invoiceRes, receiptRes] = await Promise.allSettled([
      FinanceService.getDocumentByTransactionId(token, rowId, "invoice"),
      FinanceService.getDocumentByTransactionId(token, row?.charge?._id, "receipt"),
    ]);
    setRowDocuments((prev) => ({
      ...prev,
      [rowId]: {
        invoice: invoiceRes.status === "fulfilled" ? invoiceRes.value?.result?.[0] ?? null : null,
        receipt: receiptRes.status === "fulfilled" ? receiptRes.value?.result?.[0] ?? null : null,
      },
    }));
  }, [rowDocuments]);

  const debounceSearch = useCallback(
    debounce(() => {
      if (filters?.search) fetchMyAPI();
    }, 1000),
    [filters?.search, filters]
  );

  useEffect(() => {
    if (filters?.search) {
      debounceSearch();
    } else {
      fetchMyAPI();
    }
    return () => debounceSearch.cancel();
  }, [
    filters?.search,
    filters?.status,
    filters?.filter?.charge_status,
    filters?.filter?.charge_type,
    page,
    savetribute,
    batchList,
  ]);

  useEffect(() => {
    if (Object.keys(filters?.sort)?.length > 0 || goSetting) fetchMyAPI();
  }, [filters?.sort, goSetting]);

  useEffect(() => {
    const onSaveTransaction = () => {
      setCheckData([]);
      setCheck([]);
      setFinanceCheckIds([]);
      setCurrentAccount(null);
      fetchMyAPI();
    };
    window.addEventListener("save-transaction", onSaveTransaction);
    return () => window.removeEventListener("save-transaction", onSaveTransaction);
  }, [filters, page, getAccessTokenSilently]);

  const onPayNowClick = (row) => {
    document.dispatchEvent(new CustomEvent("pay-now", { detail: row }));
  };

  const onPayAllClick = (rows) => {
    document.dispatchEvent(new CustomEvent("pay-all", { detail: rows }));
  };

  const onReversalClick = async (id, setReversalActionTypes) => {
    const token = await getAccessTokenSilently();
    await FinanceService.reverseCharge(id, token)
      .then(() => toastr.success("Success", "Charge has been reversed"))
      .catch(() =>
        toastr.error(
          "Transaction can not be reversed, please select one of the payments charges record to reverse"
        )
      );
    fetchMyAPI();
    setReversalActionTypes(false);
  };

  const onCheckboxChange = (e, row) => {
    const { checked } = e.target;
    if (!checked) {
      setCheck((prev) => prev.filter((id) => id !== row?.charge?._id));
      setCheckData((prev) => prev.filter((item) => item?.charge?._id !== row?.charge?._id));
      setCurrentAccount((prev) => {
        const remaining = check.filter((id) => id !== row?.charge?._id);
        return remaining.length ? prev : null;
      });
      return;
    }
    if (!currentAccount) setCurrentAccount(row?.Account?.id);
    if (currentAccount === row?.Account?.id || !currentAccount) {
      setCheck((prev) => [...prev, row?.charge?._id]);
      setCheckData((prev) => [...prev, row]);
      setFinanceCheckIds([row?.Account?.id]);
    }
  };

  const fmt = (n) => numberFormat({ number: n, decimal: 2, style: "currency" });

  const displayStatus = (row) => {
    if (row?.charge?.status === "payment") {
      if (row?.charge?.payment?.status === "partialy_applied") return "Partially Paid";
      if (row?.Charges?.amount_due === 0) return "Closed";
      return "Open";
    }
    return "Open";
  };

  /* ── Balance summary numbers ── */
  const fmtBalance = fmt(parseInt(balance?.balance) || 0);
  const fmtCredit = fmt(parseInt(balance?.creditOnAccount) || 0);
  const fmtOverdue = fmt(parseInt(balance?.overDue) || 0);

  const rows = productData ?? (batchTable === "batchtable" ? list : []);

  return (
    <div className="w-100">
      <div className="family-table-container pl-7 pr-6 pt-3 pb-22">
        {/* Balance strip — inside container so it shares the same padding as the table */}
        {balance !== null && (
          isMobile ? (
            /* Mobile: compact 3-column layout */
            <div
              className="balance-strip"
              style={{
                border: "1px solid rgb(0 46 92 / 17%)",
                borderRadius: 9,
                marginBottom: "1rem",
                display: "flex",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {[
                { color: FontColors.Green81DC91, label: "Credit on Account", value: fmtCredit },
                { color: FontColors.Blue2555EF, label: "Balance", value: fmtBalance },
                { color: FontColors.RedF1585E, label: `Overdue (${balance?.numberOfOverDueCharges ?? 0})`, value: fmtOverdue },
              ].map((item, i) => (
                <React.Fragment key={item.label}>
                  {i > 0 && <div style={{ width: 1, background: "rgb(0 46 92 / 12%)", flexShrink: 0, margin: "8px 0" }} />}
                  <div style={{ flex: 1, padding: "8px 8px", display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "flex-start", gap: 4, fontSize: Fonts.GeneralTinyFont.fontSize, color: FontColors.Slate6B7280, lineHeight: 1.3 }}>
                      <span className="dot" style={{ "--my-color": item.color, flexShrink: 0, marginTop: 2 }} />
                      {item.label}
                    </span>
                    <span style={{ fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight, fontSize: Fonts.GeneralSmallHeaderFont.fontSize, color: FontColors.Blue002E5C, paddingLeft: 16, whiteSpace: "nowrap" }}>
                      {item.value}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : (
            /* Desktop: original compact inline layout */
            <div
              className="balance-strip"
              style={{
                border: "1px solid rgb(0 46 92 / 17%)",
                borderRadius: 9,
                padding: "12px 20px",
                width: "fit-content",
                maxWidth: "100%",
                marginBottom: "1rem",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px 0",
                alignItems: "center",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <span className="dot" style={{ "--my-color": "#81DC91" }} />
                Credit on Account <span className="amountBold">{fmtCredit}</span>
              </span>
              <span style={{ margin: "0 10px", color: FontColors.SlateCBD5E1 }}>|</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <span className="dot" style={{ "--my-color": "#2555EF" }} />
                Balance <span className="amountBold">{fmtBalance}</span>
              </span>
              <span style={{ margin: "0 10px", color: FontColors.SlateCBD5E1 }}>|</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <span className="dot" style={{ "--my-color": "#F1585E" }} />
                Over Due({balance?.numberOfOverDueCharges ?? 0}) <span className="amountBold">{fmtOverdue}</span>
              </span>
            </div>
          )
        )}
        <div className="mt-0 no-box-shadow">
          {/* Header row */}
          <div className="d-flex justify-content-between mb-5 h-40px">
            <div className="yah-sub">Charges</div>
            {checkData?.length >= 1 && (
              <Button onClick={() => onPayAllClick(checkData)} variant="primary" size="md">
                <PayNowSvg />
                <span>{checkData?.length === 1 ? "Pay" : "Pay All"}</span>
              </Button>
            )}
          </div>

          {loading ? (
            <div className="tran-loader" style={{ textAlign: "center", padding: "40px 0" }}>
              <Loader />
            </div>
          ) : !rows?.length ? (
            <div className="record-text" style={{ textAlign: "center", padding: "24px 0" }}>No record found</div>
          ) : isMobile ? (
            /* ── Mobile: card list matching app design ── */
            <div style={{ marginTop: 4 }}>
              {rows.map((row, i) => {
                const isChecked = check.includes(row?.charge?._id);
                const isActiveInstallment = row?.charge?.installmentDetails?.status === "active";
                const isDisabled =
                  isActiveInstallment ||
                  (financeCheckIds?.length >= 1 &&
                    !financeCheckIds?.includes(row?.Account?.id) &&
                    check.length >= 1) ||
                  (row?.charge?.status === "payment" &&
                    row?.charge?.sub_status !== "partially_paid" &&
                    row?.charge?.sub_status !== "open");
                const subLedgerDisplay =
                  row?.SubLedger === null &&
                  row?.comment_invoice?.visible === true &&
                  row?.comment_invoice?.note !== null
                    ? row?.comment_invoice?.note
                    : row?.SubLedger?.name || null;
                const title = `${row?.Ledger?.name || ""}${subLedgerDisplay ? ` - ${subLedgerDisplay}` : ""}`;
                const installmentCycle = row?.charge?.installmentDetails?.cycle;
                const dueStatus = displayStatus(row);
                const amountDue = +row?.charge?.amount + (row?.charge?.discount ?? 0) - (row?.charge?.paid ?? 0);

                return (
                  <div
                    key={row._id ?? i}
                    style={{
                      borderBottom: "1px solid #F1F5F9",
                      padding: "14px 0",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      backgroundColor: isChecked ? "#F0F4FF" : "transparent",
                    }}
                  >
                    {batchTable !== "batchtable" && (
                      <div
                        style={{ paddingTop: 2, flexShrink: 0 }}
                        onClick={(e) => { e.stopPropagation(); if (isActiveInstallment) setOpenInstallmentModal(true); }}
                      >
                        <MyCheckbox
                          name="visible"
                          checked={isChecked}
                          disabled={isDisabled}
                          handlechange={(e) => onCheckboxChange(e, row)}
                        />
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setDetailRow(row)}>
                      {/* Title + actions */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ fontWeight: Fonts.GeneralSubHeaderFont.fontWeight, fontSize: Fonts.GeneralSubHeaderFont.fontSize, color: FontColors.Slate0F172A, lineHeight: 1.3 }}>{title}</div>
                        <div onClick={(e) => e.stopPropagation()}>
                        <Action
                          style={{ width: "16px" }}
                          action="finance"
                          onEdit="edit"
                          actionDrawer="table"
                          chargeStatus={row?.charge?.payment?.status}
                          mainChargeStatus={row?.charge?.status}
                          row={row}
                          onOpen={() => fetchRowDocuments(row)}
                          docsLoaded={rowDocuments[row._id] !== undefined}
                          hideInvoiceButtons={!rowDocuments[row._id]?.invoice}
                          hideReceiptButtons={!rowDocuments[row._id]?.receipt}
                          setInvoiceFile={() => viewDocumentHandler(rowDocuments[row._id]?.invoice?._id)}
                          setInvoiceDownload={() => downloadFileHandler(rowDocuments[row._id]?.invoice)}
                          setReceiptFile={() => viewDocumentHandler(rowDocuments[row._id]?.receipt?._id)}
                          setReceiptDownload={() => downloadFileHandler(rowDocuments[row._id]?.receipt)}
                          onPayClick={() => {
                            if (row?.charge?.installmentDetails?.status === "active") {
                              setOpenInstallmentModal(true);
                              return;
                            }
                            localStorage.setItem("payNowItem", JSON.stringify(row));
                            onPayNowClick(row);
                          }}
                          setBatchList={setBatchList}
                        />
                        </div>
                      </div>

                      {/* Due date */}
                      {row?.charge?.due_date && (
                        <div style={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize, color: FontColors.Slate64748B, marginTop: 4 }}>
                          Due by {moment(row?.charge?.due_date).utc().format("MMM D, YYYY")}
                        </div>
                      )}

                      {/* Billed / Paid / Due */}
                      <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: Fonts.GeneralSmallBodyFont.fontSize }}>
                        <span style={{ color: FontColors.Slate64748B }}>Billed <span style={{ fontWeight: FontWeights.SemiBold, color: FontColors.Slate334155 }}>{fmt(row?.charge?.amount)}</span></span>
                        <span style={{ color: FontColors.Slate64748B }}>Paid <span style={{ fontWeight: FontWeights.SemiBold, color: FontColors.Slate334155 }}>{row?.charge?.paid ? fmt(row?.charge?.paid) : fmt(0)}</span></span>
                        <span style={{ color: FontColors.Slate64748B }}>Due <span style={{ fontWeight: FontWeights.SemiBold, color: FontColors.Slate0F172A }}>{isNaN(amountDue) ? fmt(0) : fmt(amountDue)}</span></span>
                      </div>

                      {/* Installment badge + Active Payment Plan */}
                      {isActiveInstallment && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                          {installmentCycle && (
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 20,
                              padding: "2px 10px", fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate64748B,
                            }}>
                              ↺ {installmentCycle} month
                            </span>
                          )}
                          {dueStatus === "Partially Paid" && (
                            <span style={{ fontSize: Fonts.GeneralSmallLabelFont.fontSize, color: FontColors.Green16A34A, fontWeight: Fonts.GeneralSmallLabelFont.fontWeight }}>
                              Active Payment Plan
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          ) : (
            /* ── Desktop: same fields as mobile ── */
            <TableContainer component={Paper} className="scroll-table">
              <Table aria-label="charges table">
                <TableHead>
                  <TableRow>
                    {batchTable !== "batchtable" && <TableCellHead padding="checkbox" />}
                    <TableCellHead>
                      <Tooltip title="Charge description and ledger account" placement="top"><span>DESCRIPTION</span></Tooltip>
                    </TableCellHead>
                    <TableCellHead>
                      <Tooltip title="Date the payment is due" placement="top"><span>DUE DATE</span></Tooltip>
                    </TableCellHead>
                    <TableCellHead>
                      <Tooltip title="Total amount billed on this charge" placement="top"><span>BILLED</span></Tooltip>
                    </TableCellHead>
                    <TableCellHead>
                      <Tooltip title="Amount paid to date" placement="top"><span>PAID</span></Tooltip>
                    </TableCellHead>
                    <TableCellHead>
                      <Tooltip title="Outstanding balance remaining" placement="top"><span>DUE</span></Tooltip>
                    </TableCellHead>
                    <TableCellHead />
                  </TableRow>
                </TableHead>
                <TableBody style={{ backgroundColor: "#fff" }}>
                  {rows.map((row, i) => {
                    const isChecked = check.includes(row?.charge?._id);
                    const isActiveInstallment = row?.charge?.installmentDetails?.status === "active";
                    const isDisabled =
                      isActiveInstallment ||
                      (financeCheckIds?.length >= 1 &&
                        !financeCheckIds?.includes(row?.Account?.id) &&
                        check.length >= 1) ||
                      (row?.charge?.status === "payment" &&
                        row?.charge?.sub_status !== "partially_paid" &&
                        row?.charge?.sub_status !== "open");
                    const subLedgerDisplay =
                      row?.SubLedger === null &&
                      row?.comment_invoice?.visible === true &&
                      row?.comment_invoice?.note !== null
                        ? row?.comment_invoice?.note
                        : row?.SubLedger?.name || null;
                    const title = `${row?.Ledger?.name || ""}${subLedgerDisplay ? ` - ${subLedgerDisplay}` : ""}`;
                    const installmentCycle = row?.charge?.installmentDetails?.cycle;
                    const dueStatus = displayStatus(row);
                    const amountDue = +row?.charge?.amount + (row?.charge?.discount ?? 0) - (row?.charge?.paid ?? 0);

                    return (
                      <TableRow
                        key={row._id ?? i}
                        hover
                        onClick={() => setDetailRow(row)}
                        style={{ backgroundColor: isChecked ? "#F0F4FF" : "#fff", cursor: "pointer" }}
                      >
                        {batchTable !== "batchtable" && (
                          <TableCellBody
                            padding="checkbox"
                            onClick={(e) => { e.stopPropagation(); if (isActiveInstallment) setOpenInstallmentModal(true); }}
                            style={{ cursor: isActiveInstallment ? "pointer" : undefined }}
                          >
                            <MyCheckbox
                              name="visible"
                              checked={isChecked}
                              disabled={isDisabled}
                              handlechange={(e) => onCheckboxChange(e, row)}
                            />
                          </TableCellBody>
                        )}

                        {/* DESCRIPTION */}
                        <TableCellBody>
                          <div style={{ fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight, fontSize: Fonts.GeneralSmallHeaderFont.fontSize, color: FontColors.Slate0F172A }}>{title}</div>
                          {isActiveInstallment && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                              {installmentCycle && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 20,
                                  padding: "2px 10px", fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate64748B, whiteSpace: "nowrap",
                                }}>
                                  ↺ {installmentCycle} month
                                </span>
                              )}
                              {dueStatus === "Partially Paid" && (
                                <span style={{ fontSize: Fonts.GeneralSmallLabelFont.fontSize, color: FontColors.Green16A34A, fontWeight: Fonts.GeneralSmallLabelFont.fontWeight, whiteSpace: "nowrap" }}>
                                  Active Payment Plan
                                </span>
                              )}
                            </div>
                          )}
                        </TableCellBody>

                        {/* DUE DATE */}
                        <TableCellBody>
                          <div style={{ color: FontColors.Slate64748B, fontSize: Fonts.GeneralSmallBodyFont.fontSize, whiteSpace: "nowrap" }}>
                            {row?.charge?.due_date
                              ? moment(row?.charge?.due_date).utc().format("MMM D, YYYY")
                              : ""}
                          </div>
                        </TableCellBody>

                        {/* BILLED */}
                        <TableCellBody>
                          <div style={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize, color: FontColors.Slate334155, whiteSpace: "nowrap" }}>
                            {fmt(row?.charge?.amount)}
                          </div>
                        </TableCellBody>

                        {/* PAID */}
                        <TableCellBody>
                          <div style={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize, color: FontColors.Slate334155, whiteSpace: "nowrap" }}>
                            {row?.charge?.paid ? fmt(row?.charge?.paid) : fmt(0)}
                          </div>
                        </TableCellBody>

                        {/* DUE */}
                        <TableCellBody>
                          <div style={{ fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight, fontSize: Fonts.GeneralSmallHeaderFont.fontSize, color: FontColors.Slate0F172A, whiteSpace: "nowrap" }}>
                            {isNaN(amountDue) ? fmt(0) : fmt(amountDue)}
                          </div>
                        </TableCellBody>

                        {/* ACTIONS */}
                        <TableCellBody align="right" onClick={(e) => e.stopPropagation()}>
                          <Action
                            style={{ width: "16px" }}
                            action="finance"
                            onEdit="edit"
                            actionDrawer="table"
                            chargeStatus={row?.charge?.payment?.status}
                            mainChargeStatus={row?.charge?.status}
                            row={row}
                            onOpen={() => fetchRowDocuments(row)}
                            docsLoaded={rowDocuments[row._id] !== undefined}
                            hideInvoiceButtons={!rowDocuments[row._id]?.invoice}
                            hideReceiptButtons={!rowDocuments[row._id]?.receipt}
                            setInvoiceFile={() => viewDocumentHandler(rowDocuments[row._id]?.invoice?._id)}
                            setInvoiceDownload={() => downloadFileHandler(rowDocuments[row._id]?.invoice)}
                            setReceiptFile={() => viewDocumentHandler(rowDocuments[row._id]?.receipt?._id)}
                            setReceiptDownload={() => downloadFileHandler(rowDocuments[row._id]?.receipt)}
                            onPayClick={() => {
                              if (row?.charge?.installmentDetails?.status === "active") {
                                setOpenInstallmentModal(true);
                                return;
                              }
                              localStorage.setItem("payNowItem", JSON.stringify(row));
                              onPayNowClick(row);
                            }}
                            setBatchList={setBatchList}
                          />
                        </TableCellBody>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {batchTable !== "batchtable" && count > 0 && (
            <div className="pagination-custom mt-2">
              <Pagination
                count={Math.ceil(count / 30)}
                color="primary"
                variant="outlined"
                onChange={(event, value) => setPage(value - 1)}
                page={page + 1}
              />
            </div>
          )}
        </div>

        {/* Installment modal */}
        {openInstallmentModal && (
          <NewDialog
            isOpen={openInstallmentModal}
            handleClose={() => setOpenInstallmentModal(false)}
            content={
              <p style={{ textTransform: "none" }}>
                This charge cannot be paid online because it is part of an active payment plan.
                If you would like to make changes, please contact us.
              </p>
            }
          >
            <div className="payment-modal">
              <button
                className="payment-modal-btn payment-modal-btn-primary"
                onClick={() => setOpenInstallmentModal(false)}
              >
                Ok
              </button>
            </div>
          </NewDialog>
        )}

        {/* Row detail modal */}
        {detailRow && (() => {
          const isInstallment = detailRow?.charge?.installmentDetails?.status === "active";
          const instDetails = detailRow?.charge?.installmentDetails;
          const paymentHistory = detailRow?.charge?.payment?.History ?? [];

          const DetailRow = ({ label, value }) => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
              <span style={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.Slate94A3B8 }}>{label}</span>
              <span style={{ ...Fonts.GeneralLabelFont, color: FontColors.Blue1E3A5F }}>{value}</span>
            </div>
          );

          return (
            <Dialog
              open={!!detailRow}
              onClose={() => setDetailRow(null)}
              fullWidth
              maxWidth="sm"
              PaperProps={{ style: { borderRadius: 20, overflow: "hidden" } }}
              sx={{ "& .MuiDialog-container": { alignItems: "flex-end" }, "& .MuiDialog-paper": { width: "100%", maxWidth: 480, margin: "0 auto" } }}
            >
              <DialogContent style={{ padding: "8px 16px 20px", paddingLeft: "16px", paddingRight: "16px" }} classes={{ root: "charge-detail-dialog-content" }}>
                {/* Drag handle */}
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 8, paddingBottom: 16 }}>
                  <div style={{ width: 40, height: 4, borderRadius: 2, background: "#E2E8F0" }} />
                </div>

                {isInstallment && (
                  <>
                    <div style={{ ...Fonts.GeneralHeaderFont, color: FontColors.Blue1E3A5F, marginBottom: 20 }}>
                      Payment Plan Details
                    </div>
                    <DetailRow label="Charged Amount" value={fmt(instDetails?.amount ?? detailRow?.charge?.amount)} />
                    <DetailRow label="Amount Paid" value={fmt(instDetails?.amount_paid ?? detailRow?.charge?.paid ?? 0)} />
                    {instDetails?.start_date && (
                      <DetailRow label="Start Date" value={moment(instDetails.start_date).format("MMM D, YYYY")} />
                    )}
                    {instDetails?.end_date && (
                      <DetailRow label="End Date" value={moment(instDetails.end_date).format("MMM D, YYYY")} />
                    )}
                    <div style={{ marginTop: 20 }} />
                  </>
                )}

                <div style={{ ...Fonts.GeneralHeaderFont, color: FontColors.Blue1E3A5F, marginBottom: 12 }}>
                  Payment History
                </div>

                {paymentHistory.length === 0 ? (
                  <div style={{ fontFamily: Fonts.GeneralBodyFont.fontFamily, fontSize: Fonts.GeneralBodyFont.fontSize, color: FontColors.Slate94A3B8, padding: "8px 0" }}>
                    No payments recorded
                  </div>
                ) : (
                  paymentHistory.map((p, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: idx < paymentHistory.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                      <span style={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.Slate94A3B8 }}>
                        {p.created_at ? moment(p.created_at).format("MMM D, YYYY") : "—"}
                      </span>
                      <span style={{ ...Fonts.GeneralLargeBodyFont, color: FontColors.Blue1E3A5F }}>
                        {fmt(p.amount)}
                      </span>
                    </div>
                  ))
                )}

                <div style={{ marginTop: 32 }}>
                  <button
                    onClick={() => setDetailRow(null)}
                    style={{
                      width: "100%", padding: "18px 0", borderRadius: 14,
                      background: "#3D57F5", color: FontColors.White, border: "none",
                      ...Fonts.GeneralHeaderFont, cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}
      </div>
    </div>
  );
};

export default memo(ChargesTable);
